import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import RouteMap from "@/components/RouteMap";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import api from "@/lib/api";
import { SPORTS } from "@/lib/sports";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import { MapPin, Crosshair, Lock, GlobeHemisphereEast, Crown, Clock, TennisBall } from "@phosphor-icons/react";

export default function CreateActivity() {
  const nav = useNavigate();
  const { id: editId } = useParams(); // present when route is /activity/:id/edit
  const isEdit = Boolean(editId);
  const { t, lang } = useI18n();
  const [sport, setSport] = useState("cycling");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [skill, setSkill] = useState(SPORTS[0].levels[0]);
  const [maxP, setMaxP] = useState(6);
  const [isPrivate, setIsPrivate] = useState(false);
  const [startsAt, setStartsAt] = useState(() => {
    const d = new Date(Date.now() + 24 * 3600 * 1000);
    d.setMinutes(0); d.setSeconds(0); d.setMilliseconds(0);
    return d.toISOString().slice(0, 16);
  });
  const [startPt, setStartPt] = useState(null);
  const [endPt, setEndPt] = useState(null);
  const [startText, setStartText] = useState("");
  const [endText, setEndText] = useState("");
  const [pickMode, setPickMode] = useState("start");
  const [submitting, setSubmitting] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(isEdit);
  // Live route preview (recomputed whenever start/end/sport change)
  const [preview, setPreview] = useState({ distance_km: null, duration_min: null, geometry: null });
  const [pro, setPro] = useState({ is_pro: false, free_max_participants: 10, pro_max_participants: 50 });

  useEffect(() => { api.get("/pro/me").then((r) => setPro(r.data)).catch(() => {}); }, []);
  const cap = pro.is_pro ? pro.pro_max_participants : pro.free_max_participants;

  // When editing, prefill the form from the existing activity.
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/activities/${editId}`);
        setSport(data.sport);
        setTitle(data.title || "");
        setDescription(data.description || "");
        setSkill(data.skill_level || SPORTS[0].levels[0]);
        setMaxP(data.max_participants);
        setIsPrivate(Boolean(data.is_private));
        if (data.starts_at) {
          // datetime-local needs `YYYY-MM-DDTHH:mm` in local time
          const d = new Date(data.starts_at);
          if (!isNaN(d)) {
            const pad = (n) => String(n).padStart(2, "0");
            setStartsAt(
              `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
            );
          }
        }
        if (data.start_point) {
          setStartPt({ ...data.start_point, label: "Start" });
          setStartText(data.start_point.address || "");
        }
        if (data.end_point) {
          setEndPt({ ...data.end_point, label: "End" });
          setEndText(data.end_point.address || "");
        }
      } catch (e) {
        toast.error(lang === "da" ? "Kunne ikke hente session" : "Couldn't load session");
        nav("/dashboard");
      } finally {
        setLoadingEdit(false);
      }
    })();
  }, [isEdit, editId, lang, nav]);

  const sportObj = useMemo(() => SPORTS.find((s) => s.key === sport), [sport]);
  // Reset skill when sport changes — but NOT during the initial edit prefill, or
  // we'd overwrite the saved skill with the first level of the new sport.
  useEffect(() => {
    if (loadingEdit) return;
    if (!sportObj.levels.includes(skill)) setSkill(sportObj.levels[0]);
  }, [sportObj, loadingEdit]); // eslint-disable-line react-hooks/exhaustive-deps

  // Switching TO padel: drop the end-point (padel is venue-only). Switching
  // OUT of padel keeps whatever start/end the user had — they may want to
  // re-use the start point as a route origin.
  useEffect(() => {
    if (loadingEdit) return;
    if (sport === "padel") {
      setEndPt(null);
      setEndText("");
      setPickMode("start");
    }
  }, [sport, loadingEdit]);

  const onMapClick = async ({ lat, lng }) => {
    // 1) snap the marker immediately so the UX feels instant
    const isStart = pickMode === "start";
    const provisional = { lat, lng, label: isStart ? "Start" : "End" };
    if (isStart) {
      setStartPt(provisional);
      setStartText(lang === "da" ? "Indlæser adresse…" : "Loading address…");
    } else {
      setEndPt(provisional);
      setEndText(lang === "da" ? "Indlæser adresse…" : "Loading address…");
    }
    // 2) reverse-geocode so the text field shows a readable address
    try {
      const { data } = await api.get("/geo/reverse", { params: { lat, lng } });
      const address = data.display_name || data.short_label || "";
      const enriched = { ...provisional, address, lat: data.lat || lat, lng: data.lng || lng };
      if (isStart) { setStartPt(enriched); setStartText(address); }
      else         { setEndPt(enriched);   setEndText(address); }
    } catch {
      // network/backend failure — keep the coords, clear the loading text
      if (isStart) setStartText("");
      else setEndText("");
    }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        onMapClick({ lat: pos.coords.latitude, lng: pos.coords.longitude });
      },
      () => toast.error("Couldn't get your location")
    );
  };

  // Padel is venue-based — no route, no end-point, no distance/duration.
  // For cycling & running we keep the existing route-planner UX.
  const isRouteSport = sport !== "padel";

  // ── Live route preview: when start, end, or sport changes, ask the backend
  // for a fresh OSRM route. Debounced so rapid edits don't hammer the API.
  // Distance / duration in the right-hand summary card update reactively.
  // Skipped entirely for padel (no route to draw).
  useEffect(() => {
    if (!isRouteSport || !startPt || !endPt) {
      setPreview({ distance_km: null, duration_min: null, geometry: null });
      return;
    }
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post(
          "/route/preview",
          {
            sport,
            start: { lat: startPt.lat, lng: startPt.lng },
            end: { lat: endPt.lat, lng: endPt.lng },
          },
          { signal: ctrl.signal },
        );
        setPreview({
          distance_km: data.distance_km ?? null,
          duration_min: data.duration_min ?? null,
          geometry: data.geometry || null,
        });
      } catch (err) {
        if (err.name !== "CanceledError" && err.code !== "ERR_CANCELED") {
          // soft-fail — user can still publish, backend will recompute on save
        }
      }
    }, 300);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [startPt, endPt, sport, isRouteSport]);

  const submit = async (e) => {
    e?.preventDefault();
    if (isRouteSport && !startPt) { toast.error(t("create.need_start")); return; }
    if (!title.trim()) { toast.error(t("create.need_title")); return; }
    setSubmitting(true);
    try {
      const startBody = startPt
        ? { lat: startPt.lat, lng: startPt.lng, label: startPt.label || (isRouteSport ? "Start" : "Venue"), address: startPt.address }
        : null;
      const endBody = (isRouteSport && endPt)
        ? { lat: endPt.lat, lng: endPt.lng, label: endPt.label || "End", address: endPt.address }
        : null;

      if (isEdit) {
        const payload = {
          sport, title, description,
          starts_at: new Date(startsAt).toISOString(),
          max_participants: Number(maxP),
          skill_level: skill,
          is_private: isPrivate,
        };
        if (startBody) payload.start_point = startBody;
        if (endBody) {
          payload.end_point = endBody;
        } else {
          payload.clear_end_point = true;
        }
        const { data } = await api.patch(`/activities/${editId}`, payload);
        toast.success(lang === "da" ? "Session opdateret" : "Session updated");
        nav(`/activity/${data.activity_id}`);
      } else {
        const payload = {
          sport, title, description,
          starts_at: new Date(startsAt).toISOString(),
          max_participants: Number(maxP),
          skill_level: skill,
          is_private: isPrivate,
        };
        if (startBody) payload.start_point = startBody;
        if (endBody)   payload.end_point   = endBody;
        const { data } = await api.post("/activities", payload);
        toast.success(t("create.created"));
        nav(`/activity/${data.activity_id}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || (isEdit ? (lang === "da" ? "Kunne ikke gemme" : "Couldn't save") : "Couldn't create"));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="label-overline text-[#007AFF]">
          {isEdit ? (lang === "da" ? "Rediger" : "Edit") : t("create.kicker")}
        </div>
        <h1 className="font-display text-5xl tracking-tight mt-2">
          {isEdit ? (lang === "da" ? "REDIGER SESSION." : "EDIT SESSION.") : t("create.title")}
        </h1>

        {loadingEdit ? (
          <div className="mt-10 text-zinc-400 text-sm">{lang === "da" ? "Indlæser…" : "Loading…"}</div>
        ) : (
        <form onSubmit={submit} className="mt-8 grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2 space-y-5">
            <FieldGroup label={t("create.sport")}>
              <div className="grid grid-cols-3 gap-2">
                {SPORTS.map((s) => (
                  <button type="button" key={s.key} data-testid={`create-sport-${s.key}`} onClick={() => setSport(s.key)}
                    className={`kinetic-btn py-3 uppercase tracking-widest text-xs font-bold border ${sport === s.key ? "bg-[#007AFF] border-[#007AFF]" : "border-[#262626] hover:border-white/40 bg-[#141414]"}`}>
                    {s.label}
                  </button>
                ))}
              </div>
            </FieldGroup>

            <FieldGroup label={t("create.title_field")}>
              <input data-testid="create-title" required value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("create.title_placeholder")}
                className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#007AFF] px-3 py-3 text-sm outline-none" />
            </FieldGroup>

            <FieldGroup label={t("create.description")}>
              <textarea data-testid="create-description" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder={t("create.description_placeholder")}
                className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#007AFF] px-3 py-3 text-sm outline-none resize-none" />
            </FieldGroup>

            <div className="grid grid-cols-2 gap-3">
              <FieldGroup label={t("create.datetime")}>
                <input data-testid="create-startsat" type="datetime-local" required value={startsAt} onChange={(e) => setStartsAt(e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#007AFF] px-3 py-3 text-sm outline-none" />
              </FieldGroup>
              <FieldGroup label={t("create.max_participants")}>
                <input data-testid="create-max" type="number" min={2} max={cap} required value={maxP} onChange={(e) => setMaxP(e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#007AFF] px-3 py-3 text-sm outline-none" />
                {!pro.is_pro && (
                  <div className="mt-2 text-[11px] text-zinc-500 flex items-center gap-2">
                    <span>{lang === "da" ? `Free-grænse: ${cap}.` : `Free cap: ${cap}.`}</span>
                    <Link to="/pro" data-testid="create-upgrade-link" className="text-[#FFCC00] hover:underline inline-flex items-center gap-1">
                      <Crown weight="fill" size={11} /> {lang === "da" ? "Bliv Pro for op til 50" : "Go Pro for up to 50"}
                    </Link>
                  </div>
                )}
              </FieldGroup>
            </div>

            <FieldGroup label={t("create.skill_level")}>
              <div className="flex flex-wrap gap-2">
                {sportObj.levels.map((l) => (
                  <button type="button" key={l} data-testid={`create-skill-${l}`} onClick={() => setSkill(l)}
                    className={`kinetic-btn px-3 py-2 uppercase tracking-widest text-xs font-bold border ${skill === l ? "bg-[#007AFF] border-[#007AFF]" : "border-[#262626] hover:border-white/40 bg-[#141414]"}`}>
                    {l}
                  </button>
                ))}
              </div>
            </FieldGroup>

            <FieldGroup label={t("create.visibility")}>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" data-testid="create-visibility-public" onClick={() => setIsPrivate(false)}
                  className={`kinetic-btn flex items-center justify-center gap-2 py-3 uppercase tracking-widest text-xs font-bold border ${!isPrivate ? "bg-[#007AFF] border-[#007AFF]" : "border-[#262626] hover:border-white/40 bg-[#141414]"}`}>
                  <GlobeHemisphereEast weight="bold" size={14} /> {t("common.public")}
                </button>
                <button type="button" data-testid="create-visibility-private" onClick={() => setIsPrivate(true)}
                  className={`kinetic-btn flex items-center justify-center gap-2 py-3 uppercase tracking-widest text-xs font-bold border ${isPrivate ? "bg-[#007AFF] border-[#007AFF]" : "border-[#262626] hover:border-white/40 bg-[#141414]"}`}>
                  <Lock weight="bold" size={14} /> {t("common.private")}
                </button>
              </div>
            </FieldGroup>

            <button data-testid="create-submit" disabled={submitting} className="kinetic-btn w-full bg-[#007AFF] hover:bg-[#0a66d0] disabled:opacity-50 text-white px-4 py-3 font-bold uppercase tracking-widest text-sm">
              {submitting
                ? (isEdit ? (lang === "da" ? "Gemmer…" : "Saving…") : t("create.publishing"))
                : (isEdit ? (lang === "da" ? "Gem ændringer" : "Save changes") : t("create.publish"))}
            </button>
          </div>

          <div className="lg:col-span-3 space-y-3">
            {isRouteSport ? (
              <>
                {/* Address autocomplete inputs — same UX as the mobile app: type
                    to search, or click the map / "My location" to backfill. */}
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <div className="label-overline mb-2" style={{ color: "#007AFF" }}>
                      {lang === "da" ? "Startadresse" : "Start address"}
                    </div>
                    <AddressAutocomplete
                      value={startText}
                      onTextChange={setStartText}
                      onSelect={({ lat, lng, address }) => {
                        setStartPt({ lat, lng, address, label: "Start" });
                        setPickMode("end");
                      }}
                      accent="#007AFF"
                      placeholder={lang === "da" ? "Søg startadresse…" : "Search start address…"}
                      testId="address-start"
                    />
                  </div>
                  <div>
                    <div className="label-overline mb-2" style={{ color: "#FF3B30" }}>
                      {lang === "da" ? "Slutadresse (valgfri)" : "End address (optional)"}
                    </div>
                    <AddressAutocomplete
                      value={endText}
                      onTextChange={setEndText}
                      onSelect={({ lat, lng, address }) => {
                        setEndPt({ lat, lng, address, label: "End" });
                      }}
                      accent="#FF3B30"
                      placeholder={lang === "da" ? "Søg slutadresse…" : "Search end address…"}
                      testId="address-end"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between flex-wrap gap-2 pt-2">
                  <div className="label-overline">{t("create.route_hint")}</div>
                  <div className="flex gap-2">
                    <button type="button" data-testid="pick-start" onClick={() => setPickMode("start")} className={`kinetic-btn px-3 py-1.5 text-xs uppercase tracking-widest font-bold border ${pickMode === "start" ? "bg-[#007AFF] border-[#007AFF]" : "border-[#262626] hover:border-white/40 bg-[#141414]"}`}>
                      <MapPin weight="bold" size={12} className="inline mr-1" /> {t("create.start")}
                    </button>
                    <button type="button" data-testid="pick-end" onClick={() => setPickMode("end")} className={`kinetic-btn px-3 py-1.5 text-xs uppercase tracking-widest font-bold border ${pickMode === "end" ? "bg-[#FF3B30] border-[#FF3B30]" : "border-[#262626] hover:border-white/40 bg-[#141414]"}`}>
                      <MapPin weight="bold" size={12} className="inline mr-1" /> {t("create.end")}
                    </button>
                    <button type="button" data-testid="pick-mylocation" onClick={useMyLocation} className="kinetic-btn px-3 py-1.5 text-xs uppercase tracking-widest font-bold border border-[#262626] hover:border-white/40 bg-[#141414]">
                      <Crosshair weight="bold" size={12} className="inline mr-1" /> {t("create.my_location")}
                    </button>
                  </div>
                </div>
                <RouteMap start={startPt} end={endPt} geometry={preview.geometry} height={420} onMapClick={onMapClick} />

                {/* Live route summary — distance + duration update as user types */}
                <div className="grid grid-cols-2 gap-3" data-testid="route-summary">
                  <div className="border border-[#262626] bg-[#141414] p-3 flex items-center gap-3">
                    <MapPin weight="duotone" size={20} color="#007AFF" />
                    <div>
                      <div className="label-overline" style={{ color: "#007AFF" }}>{lang === "da" ? "Distance" : "Distance"}</div>
                      <div className="font-display text-2xl tracking-tight">
                        {preview.distance_km != null ? `${preview.distance_km} km` : "—"}
                      </div>
                    </div>
                  </div>
                  <div className="border border-[#262626] bg-[#141414] p-3 flex items-center gap-3">
                    <Clock weight="duotone" size={20} color="#FF3B30" />
                    <div>
                      <div className="label-overline" style={{ color: "#FF3B30" }}>{lang === "da" ? "Estimeret tid" : "Est. duration"}</div>
                      <div className="font-display text-2xl tracking-tight">
                        {preview.duration_min != null ? `${Math.round(preview.duration_min)} min` : "—"}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              /* PADEL — venue-only. No map, no route, no distance/duration. */
              <div data-testid="padel-panel" className="space-y-4">
                <div className="border border-[#34C759]/40 bg-[#34C759]/[0.06] p-5">
                  <div className="flex items-start gap-3">
                    <TennisBall weight="duotone" size={28} color="#34C759" />
                    <div>
                      <div className="label-overline" style={{ color: "#34C759" }}>
                        {lang === "da" ? "Padel-session" : "Padel session"}
                      </div>
                      <h3 className="font-display text-2xl tracking-tight mt-1">
                        {lang === "da" ? "INGEN RUTE — KUN MØDESTED." : "NO ROUTE — JUST A VENUE."}
                      </h3>
                      <p className="text-sm text-zinc-300 mt-2 max-w-xl">
                        {lang === "da"
                          ? "Padel er ikke en rute-baseret aktivitet, så vi springer kort, distance og estimeret tid over. Tilføj kun en valgfri klub eller hal nedenfor."
                          : "Padel isn't a route-based activity, so we skip the map, distance and estimated duration. Just add an optional club or hall below."}
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="label-overline mb-2" style={{ color: "#34C759" }}>
                    {lang === "da" ? "Klub / hal (valgfri)" : "Club / venue (optional)"}
                  </div>
                  <AddressAutocomplete
                    value={startText}
                    onTextChange={(v) => { setStartText(v); if (!v) setStartPt(null); }}
                    onSelect={({ lat, lng, address }) => {
                      setStartPt({ lat, lng, address, label: "Venue" });
                    }}
                    accent="#34C759"
                    placeholder={lang === "da" ? "Søg klub eller hal…" : "Search club or venue…"}
                    testId="address-venue"
                  />
                  {startPt && (
                    <div className="mt-3 border border-[#262626] bg-[#141414] p-3 flex items-start gap-3" data-testid="padel-venue-card">
                      <MapPin weight="duotone" size={20} color="#34C759" className="mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="label-overline" style={{ color: "#34C759" }}>
                          {lang === "da" ? "Valgt sted" : "Selected venue"}
                        </div>
                        <div className="text-sm text-white mt-1 truncate">{startPt.address || `${startPt.lat.toFixed(4)}, ${startPt.lng.toFixed(4)}`}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => { setStartPt(null); setStartText(""); }}
                        data-testid="padel-venue-clear"
                        className="ml-auto text-xs uppercase tracking-widest font-bold text-zinc-400 hover:text-white"
                      >
                        {lang === "da" ? "Fjern" : "Remove"}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </form>
        )}
      </main>
    </div>
  );
}

function FieldGroup({ label, children }) {
  return (
    <div>
      <div className="label-overline mb-2">{label}</div>
      {children}
    </div>
  );
}
